import type { ReactNode } from 'react';
import {
  type Control,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  useController,
} from 'react-hook-form';
import { FormField } from './form-field';

type A11y = {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': boolean | undefined;
};

/**
 * Campo de formulário ligado ao React Hook Form (§32): rótulo, dica e o erro do Zod junto do
 * campo, pelo mesmo `FormField` de sempre — o vínculo acessível entre rótulo, erro e controle
 * continua num lugar só. Recebe o `field` do RHF já somado aos atributos de acessibilidade.
 */
export function Field<
  T extends FieldValues,
  N extends FieldPath<T>,
  // Valor depois do schema (ex.: texto vazio que vira `null`); o campo lida com o de entrada.
  TOut extends FieldValues = T,
>({
  control,
  name,
  label,
  hint,
  required,
  hideLabel,
  className,
  children,
}: {
  control: Control<T, unknown, TOut>;
  name: N;
  label: string;
  hint?: string;
  required?: boolean;
  hideLabel?: boolean;
  className?: string;
  children: (field: ControllerRenderProps<T, N> & A11y) => ReactNode;
}) {
  const { field, fieldState } = useController({ control, name });
  return (
    <FormField
      label={label}
      hint={hint}
      required={required}
      hideLabel={hideLabel}
      className={className}
      error={fieldState.error?.message}
    >
      {(a11y) => children({ ...field, ...a11y })}
    </FormField>
  );
}
